import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, CalendarPlus, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { API } from "../../App";

/**
 * Log a complaint against a completed job and, in the same pass, book the
 * free redo visit. ADMIN ONLY — every endpoint it calls refuses a staff
 * token, and the fault fields never travel on the booking document the
 * staff Job Board reads.
 */
export const ComplaintRedoDialog = ({ open, onClose, booking, onSaved }) => {
  const [options, setOptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slot, setSlot] = useState(null);
  const [checking, setChecking] = useState(false);

  const [form, setForm] = useState({
    description: "", area: "", reported_date: new Date().toISOString().slice(0, 10),
    reason_category: "bubbles_contamination",
    responsible_type: "installer", responsible_user_id: "", note: "",
  });
  const [schedule, setSchedule] = useState(false);
  const [warrantyBadge, setWarrantyBadge] = useState(null);
  const [redo, setRedo] = useState({
    booking_date: "", booking_time: "10:00", duration_minutes: 60,
    technician_id: "", notify_customer: true, override: false, override_reason: "",
  });

  const warranty = booking?.warranty_duration;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/redos/options`);
        setOptions(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load the options");
      }
      try {
        const w = await axios.get(`${API}/redos/warranty/${booking?.booking_id}`);
        setWarrantyBadge(w.data);
      } catch (_e) {
        setWarrantyBadge(null);
      }
    })();
  }, [open, booking?.booking_id]);

  const checkSlot = useCallback(async () => {
    if (!redo.booking_date || !redo.booking_time) return;
    setChecking(true);
    try {
      const r = await axios.get(`${API}/redos/availability`, {
        params: {
          location_id: booking?.location_id,
          date: redo.booking_date,
          time: redo.booking_time,
          duration_minutes: redo.duration_minutes,
        },
      });
      setSlot(r.data);
    } catch (e) {
      setSlot(null);
    } finally {
      setChecking(false);
    }
  }, [booking?.location_id, redo.booking_date, redo.booking_time, redo.duration_minutes]);

  useEffect(() => { if (schedule) checkSlot(); }, [schedule, checkSlot]);

  const submit = async () => {
    if (!form.description.trim()) {
      toast.error("Describe what the customer reported");
      return;
    }
    if (schedule && (!redo.booking_date || !redo.technician_id)) {
      toast.error("A redo visit needs a date and an assigned technician");
      return;
    }
    if (schedule && slot && !slot.ok && !redo.override) {
      toast.error(`That slot is not free: ${slot.reason}. Tick "override" to book it anyway.`);
      return;
    }
    setSaving(true);
    try {
      const complaint = await axios.post(`${API}/redos/complaints`, {
        booking_id: booking.booking_id,
        description: form.description,
        area: form.area || null,
        reported_date: form.reported_date,
        reason_category: form.reason_category,
        note: form.note || null,
        responsible_party: {
          type: form.responsible_type,
          user_id: form.responsible_type === "installer"
            ? (form.responsible_user_id || null) : null,
          name: null,
        },
      });
      if (schedule) {
        await axios.post(
          `${API}/redos/complaints/${complaint.data.complaint_id}/schedule`, {
            booking_date: redo.booking_date,
            booking_time: redo.booking_time,
            duration_minutes: Number(redo.duration_minutes) || 60,
            technician_id: redo.technician_id,
            notify_customer: redo.notify_customer,
            override: redo.override,
            override_reason: redo.override_reason || null,
          });
        toast.success("Complaint logged and the redo visit is booked");
      } else {
        toast.success("Complaint logged");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the complaint");
    } finally {
      setSaving(false);
    }
  };

  const inWarranty = warrantyBadge?.in_warranty;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[88vh] overflow-y-auto"
                     data-testid="complaint-redo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Log complaint / schedule redo
            {warranty ? (
              <Badge className={inWarranty === false
                ? "bg-red-500/20 text-red-300 border-red-500/40"
                : "bg-green-500/20 text-green-300 border-green-500/40"}
                     data-testid="complaint-warranty-badge">
                {inWarranty === false
                  ? <ShieldOff className="w-3 h-3 mr-1" />
                  : <ShieldCheck className="w-3 h-3 mr-1" />}
                {warrantyBadge?.label || `In warranty · ${warranty}`}
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {booking?.vehicle_reg || booking?.vehicle?.reg} · {booking?.customer_name}.
            An out-of-warranty redo is still allowed — it is only badged.
            Fault details stay admin-only and never reach the staff job card.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">Date reported</Label>
              <Input type="date" value={form.reported_date}
                     onChange={(e) => setForm({ ...form, reported_date: e.target.value })}
                     className="bg-slate-800 border-slate-700 text-white"
                     data-testid="complaint-reported-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">Glass / area</Label>
              <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                               data-testid="complaint-area">
                  <SelectValue placeholder="Which area?" />
                </SelectTrigger>
                <SelectContent>
                  {(options?.areas || []).map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-slate-400 text-sm">What the customer reported</Label>
            <Textarea value={form.description} rows={3}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="In the customer's words…"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="complaint-description" />
          </div>

          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-3">
            <p className="text-sm text-white font-semibold">Fault record (admin only)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-sm">Reason</Label>
                <Select value={form.reason_category}
                        onValueChange={(v) => setForm({ ...form, reason_category: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                                 data-testid="complaint-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options?.reason_categories || []).map((r) => (
                      <SelectItem key={r.key} value={r.key}>
                        {r.label}{r.counts ? "" : " (never counted against an installer)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-sm">Responsible</Label>
                <Select value={form.responsible_type}
                        onValueChange={(v) => setForm({ ...form, responsible_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                                 data-testid="complaint-responsible-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="installer">Installer</SelectItem>
                    <SelectItem value="supplier">Film / supplier</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.responsible_type === "installer" && (
              <div className="space-y-1">
                <Label className="text-slate-400 text-sm">Which installer</Label>
                <Select value={form.responsible_user_id}
                        onValueChange={(v) => setForm({ ...form, responsible_user_id: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                                 data-testid="complaint-responsible-user">
                    <SelectValue placeholder="Defaults to the technician on the original job" />
                  </SelectTrigger>
                  <SelectContent>
                    {(options?.technicians || []).map((t) => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Leave blank to use the technician who did the original job.
                </p>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">Note</Label>
              <Textarea value={form.note} rows={2}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="complaint-note" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="schedule-redo" checked={schedule}
                      onCheckedChange={(v) => setSchedule(!!v)}
                      data-testid="complaint-schedule-toggle" />
            <Label htmlFor="schedule-redo" className="text-white text-sm flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-saturn-orange" />
              Book the redo visit now
            </Label>
          </div>

          {schedule && (
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-3"
                 data-testid="redo-schedule-panel">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-400 text-sm">Date</Label>
                  <Input type="date" value={redo.booking_date}
                         onChange={(e) => setRedo({ ...redo, booking_date: e.target.value })}
                         className="bg-slate-800 border-slate-700 text-white"
                         data-testid="redo-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-sm">Time</Label>
                  <Input type="time" value={redo.booking_time}
                         onChange={(e) => setRedo({ ...redo, booking_time: e.target.value })}
                         className="bg-slate-800 border-slate-700 text-white"
                         data-testid="redo-time" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-sm">Minutes</Label>
                  <Input type="number" min="15" step="15" value={redo.duration_minutes}
                         onChange={(e) => setRedo({ ...redo, duration_minutes: e.target.value })}
                         className="bg-slate-800 border-slate-700 text-white"
                         data-testid="redo-duration" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-slate-400 text-sm">Technician</Label>
                <Select value={redo.technician_id}
                        onValueChange={(v) => setRedo({ ...redo, technician_id: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white"
                                 data-testid="redo-technician">
                    <SelectValue placeholder="Who is doing it?" />
                  </SelectTrigger>
                  <SelectContent>
                    {(options?.technicians || []).map((t) => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {checking && <p className="text-xs text-slate-400">Checking the diary…</p>}
              {slot && !checking && (
                <p className={`text-xs flex items-center gap-1 ${
                  slot.ok ? "text-green-400" : "text-amber-400"}`}
                   data-testid="redo-slot-status">
                  {slot.ok
                    ? `Slot is free — ${slot.bays_free} bay(s) available`
                    : <><AlertTriangle className="w-3 h-3" />{slot.reason}</>}
                </p>
              )}

              {slot && !slot.ok && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="redo-override" checked={redo.override}
                              onCheckedChange={(v) => setRedo({ ...redo, override: !!v })}
                              data-testid="redo-override" />
                    <Label htmlFor="redo-override" className="text-amber-300 text-sm">
                      Book it anyway (recorded against your name)
                    </Label>
                  </div>
                  {redo.override && (
                    <Input value={redo.override_reason}
                           onChange={(e) => setRedo({ ...redo, override_reason: e.target.value })}
                           placeholder="Why are you overriding the diary?"
                           className="bg-slate-800 border-slate-700 text-white"
                           data-testid="redo-override-reason" />
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Checkbox id="redo-notify" checked={redo.notify_customer}
                          onCheckedChange={(v) => setRedo({ ...redo, notify_customer: !!v })}
                          data-testid="redo-notify-customer" />
                <Label htmlFor="redo-notify" className="text-white text-sm">
                  Email the customer a confirmation (says &quot;no charge&quot;)
                </Label>
              </div>
              <p className="text-xs text-slate-500">
                No price, no deposit, no invoice and no new warranty. The
                assigned technician and the admin are emailed now and again
                the day before.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  className="border-slate-700 text-slate-300"
                  data-testid="complaint-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-saturn-orange hover:bg-orange-600"
                  data-testid="complaint-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {schedule ? "Log and book redo" : "Log complaint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Complaint + redo history for one original job. Admin only. */
export const ComplaintHistory = ({ bookingId, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/redos/complaints`,
                                  { params: { booking_id: bookingId } });
        if (!cancelled) setRows(r.data.complaints || []);
      } catch (_e) {
        /* silent — history is additive context */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, refreshKey]);

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  if (!rows.length) return null;

  const STATUS_TONE = {
    open: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    scheduled: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    fixed: "bg-green-500/20 text-green-300 border-green-500/40",
    closed_no_redo: "bg-slate-600/30 text-slate-300 border-slate-600",
  };

  return (
    <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-4 space-y-3"
         data-testid="complaint-history">
      <p className="text-sm font-semibold text-white">Complaints &amp; redo visits</p>
      {rows.map((c) => (
        <div key={c.complaint_id} className="text-xs space-y-1 border-t border-slate-800 pt-2"
             data-testid={`complaint-history-${c.complaint_id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-slate-300">
              {c.reported_date} · {c.area || "area not set"}
            </span>
            <div className="flex items-center gap-1.5">
              <Badge className={`text-[10px] ${STATUS_TONE[c.status] || STATUS_TONE.open}`}>
                {c.status.replace(/_/g, " ")}
              </Badge>
              <Badge className={`text-[10px] ${c.in_warranty === false
                ? "bg-red-500/20 text-red-300 border-red-500/40"
                : "bg-green-500/20 text-green-300 border-green-500/40"}`}>
                {c.in_warranty === false ? "out of warranty" : "in warranty"}
              </Badge>
            </div>
          </div>
          <p className="text-slate-400">{c.description}</p>
          <p className="text-slate-500">
            Reason: {c.reason_category?.replace(/_/g, " ")} · responsible:{" "}
            {c.responsible_party?.type === "installer"
              ? (c.responsible_party?.name || "installer")
              : c.responsible_party?.type}
          </p>
          {c.redo && (
            <p className="text-slate-400">
              Redo {c.redo.status} · {c.redo.booking_date} {c.redo.booking_time} ·{" "}
              {c.redo.assigned_technician_name || "unassigned"}
              {c.redo.schedule_override ? " · diary overridden" : ""}
            </p>
          )}
          {c.cost && (
            <p className="text-orange-300">
              Redo cost €{Number(c.cost.amount).toFixed(2)} (materials €
              {Number(c.cost.materials_cost).toFixed(2)}, labour €
              {Number(c.cost.labour_cost).toFixed(2)}
              {c.cost.labour_reason ? ` — ${c.cost.labour_reason}` : ""}) ·{" "}
              {Number(c.cost.hours_spent || 0).toFixed(2)}h
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default ComplaintRedoDialog;
